import { memo } from 'react';
import { 
  Activity, 
  Apple, 
  Dumbbell, 
  UtensilsCrossed, 
  MessageCircle,
  Scale 
} from 'lucide-react';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { staggerDelay } from '@/lib/stagger';

const metricsItems = [
  'Peso y altura',
  'Agua Corporal',
  'Masa Grasa',
  'Masa Muscular Esquelética',
  'Metabolismo Basal',
  'Grasa Visceral',
  'Índice de Masa Corporal',
  'Evaluación de Obesidad',
  'Análisis Segmental',
];

const services = [
  {
    icon: Scale,
    title: 'Control de métricas',
    description: 'Seguimiento completo de tu composición corporal.',
    metrics: metricsItems,
    color: 'bg-rose-light',
    iconColor: 'text-rose',
  },
  {
    icon: Apple,
    title: 'Nutrición clínica y deportiva',
    description: 'Planes nutricionales adaptados a tus necesidades específicas, ya sea para mejorar tu salud general o potenciar tu rendimiento deportivo.',
    color: 'bg-rose-light',
    iconColor: 'text-rose',
  },
  {
    icon: Dumbbell,
    title: 'Asesoramiento deportivo',
    description: 'Rutinas de ejercicio personalizadas que complementan tu plan nutricional para maximizar tus resultados.',
    color: 'bg-rose-light',
    iconColor: 'text-rose',
  },
  {
    icon: UtensilsCrossed,
    title: 'Recetas y plannings',
    description: 'Menús semanales con recetas deliciosas y fáciles de preparar, adaptadas a tus gustos y objetivos.',
    color: 'bg-rose-light',
    iconColor: 'text-rose',
  },
  {
    icon: MessageCircle,
    title: 'Seguimiento personalizado',
    description: 'Acompañamiento continuo vía WhatsApp y correo electrónico para resolver dudas y mantener tu motivación.',
    color: 'bg-rose-light',
    iconColor: 'text-rose',
  },
  {
    icon: Activity,
    title: 'Análisis integral',
    description: 'Evaluación completa de tu estado de salud, hábitos y objetivos para crear un plan totalmente personalizado.',
    color: 'bg-rose-light',
    iconColor: 'text-rose',
  },
];

const Services = memo(() => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="servicios" className="section-padding gradient-section">
      <div className="container-custom">
        {/* Section Header */}
        <div 
          ref={ref}
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block text-primary font-medium mb-4">
            Servicios
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Servicios de nutrición{' '}
            <span className="text-gradient">personalizados</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Ofrezco un enfoque integral que combina nutrición, ejercicio y 
            seguimiento continuo para garantizar tu éxito.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard 
              key={service.title} 
              service={service} 
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
});

Services.displayName = 'Services';

interface ServiceCardProps {
  service: typeof services[0];
  index: number;
}

const ServiceCard = memo(({ service, index }: ServiceCardProps) => {
  const { ref, isVisible } = useScrollAnimation(0.1);
  const Icon = service.icon;

  return (
    <div
      ref={ref}
      className={`glass-card group cursor-pointer h-full flex flex-col transition-all duration-500 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      } ${staggerDelay(index)}`}
    >
      {/* Icon */}
      <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`w-7 h-7 ${service.iconColor}`} />
      </div>

      {/* Content */}
      <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors" role="heading" aria-level={3}>
        {service.title}
      </h3>
      <p className="text-muted-foreground leading-relaxed">
        {service.description}
      </p>
      
      {/* Metrics list for Control de métricas */}
      {service.metrics && (
        <ul className="mt-4 space-y-1.5">
          {service.metrics.map((metric, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
              {metric}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

ServiceCard.displayName = 'ServiceCard';

export default Services;
